import { Button as ECButton, Popover } from '@eatclub-apps/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { ReactComponent as IconMinus } from '../../assets/icons/minus.svg';
import { ReactComponent as IconMinusBig } from '../../assets/icons/minus_big.svg';
import { ReactComponent as IconPlus } from '../../assets/icons/plus.svg';
import { ReactComponent as IconPlusBig } from '../../assets/icons/plus_big.svg';
import { trackEvent } from '../../utils/analytics';
import { isNil, pluralise, range } from '../../utils/helpers';
import { Button } from '../Button';
import { Spacer } from '../Spacer';
import { Typography } from '../Typography';
import useStyles from './NumberSelectRoundStyles';

const NumberSelectRound = ({
  value,
  onIncrease,
  onDecrease,
  onSetValue,
  min,
  max,
  allowMaxPlusOne,
  unit = 'guest',
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const [viewAllAnchorEl, setViewAllAnchorEl] = useState(null);

  const minGroupCap = Math.max(0, min);
  const maxGroupCap = allowMaxPlusOne ? max + 1 : max;

  const guestRange = range(minGroupCap, maxGroupCap);
  const isOverMaxGuests = value > max;

  return (
    <Spacer
      centered
      gap={0}
      style={{
        display: 'inline-flex',
        border: `1px solid ${theme.colors.outlineColour}`,
        borderRadius: `12px`,
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <ECButton
        type='custom'
        disabled={value <= minGroupCap}
        variant='outlined'
        onClick={onDecrease}
        style={{
          height: '100%',
          button: {
            width: '40px',
            height: '38px',
            border: 'none',
            borderRadius: '8px 0 0 8px',
          },
          buttonHover: {
            // border: 'none',
            backgroundColor: '#F3F3F3',
            filter: 'none',
          },
          disabled: {
            opacity: '0.2',
            backgroundColor: 'transparent',
          },
        }}
      >
        <IconMinusBig width={12} />
      </ECButton>

      <Button
        variant='text'
        active={!isNil(viewAllAnchorEl)}
        style={{
          width: '40px',
          fontWeight: theme.fontWeights.regular,
          '&:hover': {
            backgroundColor: '#F3F3F3',
          },
          height: '38px',
          // width: '120px',
          fontSize: '18px',
          borderRadius: '0',
        }}
        onClick={(e) => {
          e.stopPropagation();
          trackEvent('Guests', 'Viewed All guest sizes');
          setViewAllAnchorEl(e.currentTarget);
        }}
      >
        <Typography className={classes.guests} centered>
          {`${value}${isOverMaxGuests ? '+' : ''} ${pluralise(unit, value)}`}
        </Typography>
      </Button>

      <ECButton
        type='custom'
        disabled={value >= maxGroupCap}
        variant='custom'
        onClick={onIncrease}
        style={{
          height: '100%',
          button: {
            width: '40px',
            height: '38px',
            border: 'none',
            borderRadius: '0 8px 8px 0',
          },
          buttonHover: {
            // border: 'none',
            backgroundColor: '#F3F3F3',
            filter: 'none',
          },
          disabled: {
            opacity: '0.2',
            backgroundColor: 'transparent',
          },
        }}
      >
        <IconPlusBig width={12} />
      </ECButton>

      <Popover
        anchorEl={viewAllAnchorEl}
        isOpen={!!viewAllAnchorEl}
        onClose={(e) => {
          e.stopPropagation();
          setViewAllAnchorEl(null);
        }}
        showArrow
        innerStyle={{
          borderRadius: '8px',
          padding: '0',
          position: 'relative',
          marginTop: '16px',
          // maxWidth: restaurant.data.premium ? !breakpointXsDown && '540px' : 'fit-content',
          width: 'fit-content',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Spacer
          gap={0}
          direction='vertical'
          style={{
            fontFamily: theme.baseTypography.availabilityFontFamily,
            padding: '8px',
            maxHeight: '360px',
          }}
        >
          {guestRange.map((size, index) => (
            <Button
              variant='text'
              key={`guest-${size}`}
              onClick={() => {
                onSetValue(size);
                setViewAllAnchorEl(null);
              }}
              style={{ fontWeight: theme.fontWeights.regular, color: theme.colors.charcoal }}
            >
              <Typography>
                {size}
                {allowMaxPlusOne && index === guestRange.length - 1 ? '+' : ''}
              </Typography>
            </Button>
          ))}
        </Spacer>
      </Popover>
    </Spacer>
  );
};

NumberSelectRound.defaultProps = {
  value: null,
  min: 0,
  max: null,
  allowMaxPlusOne: false,
  unit: 'guest',
};

NumberSelectRound.propTypes = {
  value: PropTypes.number,
  onIncrease: PropTypes.func.isRequired,
  onDecrease: PropTypes.func.isRequired,
  onSetValue: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  allowMaxPlusOne: PropTypes.bool,
  unit: PropTypes.string,
};

export default NumberSelectRound;
